import { z } from 'zod';
import { AddressType, ClearinghouseTransactionType, CountryType, CoverageType, StateType, TagType } from '../../api';

/**
 * zod mirrors of the payer-side response models.
 *
 * These intentionally mirror `packages/core/src/api/server/models/*.ts` field for field,
 * including which fields are optional. The point is to detect the moment the two diverge —
 * a schema that is looser than the generated model cannot do that.
 *
 * Enums are built from the generated enums rather than restated, so a resync of the client
 * updates the contract for free. A value outside the enum is worth reporting even though it
 * will not crash anything: an unrecognised `CoverageType` renders as a raw string in the
 * payer table, and an unrecognised `ClearinghouseTransactionType` means we are showing a
 * payer as not supporting a transaction it may in fact support.
 *
 * Unknown *extra* keys are not flagged. zod objects ignore them by default, and that is the
 * behaviour we want: the API adding a field is routine and harmless, whereas the API
 * removing or retyping one is what breaks screens.
 */

const addressSchema = z.object({
    name: z.string(),
    lines: z.array(z.string()),
    city: z.string(),
    state: z.enum(StateType),
    postal: z.string(),
    country: z.enum(CountryType).optional(),
    type: z.enum(AddressType).optional(),
});

/**
 * Five contact purposes × four channels. The purposes are load-bearing: a claim sent to the
 * appeals address is a claim that does not get paid, so a rename on the server side needs to
 * surface loudly rather than silently blank a field in the payer drawer.
 */
const payerContactSchema = z.object({
    emailClaim: z.string().optional(),
    emailAppeal: z.string().optional(),
    emailDocumentation: z.string().optional(),
    emailOverpayment: z.string().optional(),
    emailOther: z.string().optional(),
    phoneClaim: z.string().optional(),
    phoneAppeal: z.string().optional(),
    phoneDocumentation: z.string().optional(),
    phoneOverpayment: z.string().optional(),
    phoneOther: z.string().optional(),
    faxClaim: z.string().optional(),
    faxAppeal: z.string().optional(),
    faxDocumentation: z.string().optional(),
    faxOverpayment: z.string().optional(),
    faxOther: z.string().optional(),
    addressClaim: addressSchema.optional(),
    addressAppeal: addressSchema.optional(),
    addressDocumentation: addressSchema.optional(),
    addressOverpayment: addressSchema.optional(),
    addressOther: addressSchema.optional(),
});

const supportTagSchema = z.object({
    id: z.string().optional(),
    name: z.string().optional(),
    type: z.enum(TagType).optional(),
    created: z.string().optional(),
    groupId: z.string().optional(),
});

export const supportPayerSchema = z.object({
    id: z.string().optional(),
    // The one genuinely required field on the model.
    name: z.string(),
    nickname: z.string().optional(),
    description: z.string().optional(),
    contact: payerContactSchema.optional(),
    coverage: z.enum(CoverageType).optional(),
    halt: z.boolean().optional(),
    includeBillingProvider: z.boolean().optional(),
    starred: z.boolean().optional(),
    selfPay: z.boolean().optional(),
    privatePay: z.boolean().optional(),
    active: z.boolean().optional(),
    created: z.string().optional(),
    // `groupId` is the *revision* group, not a parent payer. Do not read routing into it.
    groupId: z.string().optional(),
    tags: z.array(supportTagSchema).optional(),
});

const supportClearinghouseSchema = z.object({
    id: z.string().optional(),
    name: z.string().optional(),
    active: z.boolean().optional(),
});

export const supportClearinghousePayerSchema = z.object({
    id: z.string().optional(),
    /** The payer's identifier at this clearinghouse — what the UI surfaces as "EDI number". */
    externalId: z.string().optional(),
    includeRenderingProviders: z.boolean().optional(),
    skipEligibilityMismatchCorrection: z.boolean().optional(),
    useRenderingProviderInEligibilityCheck: z.boolean().optional(),
    transactions: z.array(z.enum(ClearinghouseTransactionType)).optional(),
    clearinghouse: supportClearinghouseSchema.optional(),
    payer: supportPayerSchema.optional(),
    starred: z.boolean().optional(),
    active: z.boolean().optional(),
    created: z.string().optional(),
    groupId: z.string().optional(),
});
